import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, TrendingUp, Users, CheckCircle2, XCircle, Clock, FileText } from 'lucide-react';
import { toast } from 'sonner';
import * as GovernanceReportService from '@/services/governanceReportService';

interface AnalyticsSummary {
  company_id: string;
  company_name: string;
  total_resolutions: number;
  approved_resolutions: number;
  rejected_resolutions: number;
  avg_hours_to_approval: number | null;
  total_members: number;
  active_members: number;
}

interface ResolutionMetrics {
  resolution_id: string;
  title: string;
  created_at: string;
  approved_at: string | null;
  status: string;
  approvals: number;
  rejections: number;
  abstentions: number;
  signed_total: number;
  hours_to_decision: number | null;
}

interface BoardAnalyticsProps {
  companyId: string;
}

export function BoardAnalytics({ companyId }: BoardAnalyticsProps) {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [metrics, setMetrics] = useState<ResolutionMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  async function loadAnalytics() {
    try {
      setLoading(true);

      // Load summary analytics
      const { data: summaryData, error: summaryError } = await supabase
        .from('board_analytics_summary')
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle();

      if (summaryError) throw summaryError;

      setSummary(summaryData);

      // Load resolution metrics
      const { data: metricsData, error: metricsError } = await supabase.rpc(
        'board_resolution_metrics',
        { p_company: companyId }
      );

      if (metricsError) throw metricsError;

      setMetrics(metricsData || []);
    } catch (err: any) {
      console.error('Error loading board analytics:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (companyId) {
      loadAnalytics();
    }
  }, [companyId]);

  async function handleGenerateReport() {
    setGenerating(true);
    try {
      const html = await GovernanceReportService.generateGovernanceReport(companyId);
      const companyName = summary?.company_name || 'Company';
      GovernanceReportService.downloadReport(html, companyName);
      toast.success('Governance report generated successfully');
    } catch (error: any) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate governance report');
    } finally {
      setGenerating(false);
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Board Analytics</h2>
        <Button onClick={handleGenerateReport} disabled={generating} variant="outline">
          {generating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <FileText className="mr-2 h-4 w-4" />
              Generate Report
            </>
          )}
        </Button>
      </div>

      {/* KPI Cards */}
      {summary && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Resolutions</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.total_resolutions}</div>
              <p className="text-xs text-muted-foreground">
                {summary.approved_resolutions} approved, {summary.rejected_resolutions} rejected
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Approval Rate</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {summary.total_resolutions > 0
                  ? Math.round((summary.approved_resolutions / summary.total_resolutions) * 100)
                  : 0}
                %
              </div>
              <p className="text-xs text-muted-foreground">Of all completed resolutions</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Time to Approval</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {summary.avg_hours_to_approval
                  ? `${summary.avg_hours_to_approval.toFixed(1)}h`
                  : 'N/A'}
              </div>
              <p className="text-xs text-muted-foreground">Average decision time</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Board Members</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.active_members}</div>
              <p className="text-xs text-muted-foreground">{summary.total_members} total members</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Resolution Metrics Table */}
      <Card>
        <CardHeader>
          <CardTitle>Resolution Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          {metrics.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Approvals</TableHead>
                    <TableHead className="text-center">Rejections</TableHead>
                    <TableHead className="text-center">Abstentions</TableHead>
                    <TableHead className="text-right">Decision Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {metrics.map((metric) => (
                    <TableRow key={metric.resolution_id}>
                      <TableCell className="font-medium max-w-xs truncate">
                        {metric.title}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            metric.status === 'APPROVED'
                              ? 'bg-green-500'
                              : metric.status === 'REJECTED'
                                ? 'bg-red-500'
                                : metric.status === 'IN_CIRCULATION'
                                  ? 'bg-blue-500'
                                  : 'bg-gray-500'
                          }
                        >
                          {metric.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          {metric.approvals}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <XCircle className="h-4 w-4 text-red-600" />
                          {metric.rejections}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">{metric.abstentions}</TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {metric.hours_to_decision ? `${metric.hours_to_decision.toFixed(1)}h` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center p-8 text-sm text-muted-foreground">
              No resolution metrics available yet.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
